import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Package, Clock, ChefHat, CheckCircle, Truck, MapPin, Phone,
  Bell, BellOff, Loader2, X, RefreshCw, ExternalLink
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_CONFIG = {
  pending: { icon: Clock, color: "text-amber-500", bg: "bg-amber-50", label: "Aguardando" },
  confirmed: { icon: CheckCircle, color: "text-blue-500", bg: "bg-blue-50", label: "Confirmado" },
  preparing: { icon: ChefHat, color: "text-orange-500", bg: "bg-orange-50", label: "Preparando" },
  ready: { icon: Package, color: "text-green-500", bg: "bg-green-50", label: "Pronto!" },
  out_for_delivery: { icon: Truck, color: "text-purple-500", bg: "bg-purple-50", label: "Saiu p/ Entrega" },
  delivered: { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50", label: "Entregue" },
  completed: { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50", label: "Concluído" },
  cancelled: { icon: X, color: "text-red-500", bg: "bg-red-50", label: "Cancelado" },
};

/**
 * Order Tracking Modal
 * Shows real-time status of an order with timeline
 */
export function OrderTrackingModal({
  open,
  onClose,
  orderId,
  themeColor = "#059669"
}) {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const fetchTracking = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/push/track/${orderId}`);
      setTracking(res.data);
    } catch (err) {
      toast.error("Erro ao carregar rastreamento");
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    if (open && orderId) {
      fetchTracking();
      // Poll every 30 seconds for updates
      const interval = setInterval(fetchTracking, 30000);
      return () => clearInterval(interval);
    }
  }, [open, orderId, fetchTracking]);

  // Check if notifications are already enabled
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  const enableNotifications = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      toast.error("Seu navegador não suporta notificações");
      return;
    }

    setSubscribing(true);
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Permissão de notificações negada");
        setSubscribing(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidRes = await axios.get(`${API_URL}/api/push/vapid-key`);
      const vapidKey = vapidRes.data.public_key;

      if (!vapidKey) {
        toast.error("Notificações não configuradas no servidor");
        setSubscribing(false);
        return;
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to backend
      await axios.post(`${API_URL}/api/push/subscribe-customer`, {
        order_id: orderId,
        subscription: subscription.toJSON()
      });

      setNotificationsEnabled(true);
      toast.success("Notificações ativadas! Você será avisado quando o status mudar.");
    } catch (err) {
      console.error("Push subscription error:", err);
      toast.error("Erro ao ativar notificações");
    }
    setSubscribing(false);
  };

  if (!open) return null;

  const StatusIcon = STATUS_CONFIG[tracking?.current_status]?.icon || Clock;
  const statusConfig = STATUS_CONFIG[tracking?.current_status] || STATUS_CONFIG.pending;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto max-h-[90vh] overflow-y-auto" data-testid="order-tracking-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" style={{ color: themeColor }} />
            Rastrear Pedido
          </DialogTitle>
        </DialogHeader>

        {loading && !tracking ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : !tracking ? (
          <div className="text-center py-10 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Pedido não encontrado</p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Current Status Banner */}
            <div className={`p-4 rounded-xl ${statusConfig.bg} border`}>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full ${statusConfig.bg} border flex items-center justify-center`}>
                  <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-lg">{statusConfig.label}</p>
                  {tracking.order?.order_number && (
                    <p className="text-sm text-muted-foreground">
                      Pedido #{tracking.order.order_number}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={fetchTracking}
                  className="h-8 w-8 p-0"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Timeline */}
            <div className="space-y-0">
              <p className="text-sm font-medium text-muted-foreground mb-3">Progresso do Pedido</p>
              {tracking.timeline?.map((step, i) => {
                const StepIcon = STATUS_CONFIG[step.status]?.icon || Clock;
                const isCompleted = step.completed;
                const isCurrent = step.current;

                return (
                  <div key={step.status} className="flex items-start gap-3">
                    {/* Line connector */}
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                        isCompleted 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : isCurrent 
                          ? 'border-blue-500 bg-blue-50 animate-pulse' 
                          : 'border-gray-200 bg-gray-50'
                      }`}>
                        <StepIcon className={`w-4 h-4 ${
                          isCompleted ? 'text-emerald-500' : isCurrent ? 'text-blue-500' : 'text-gray-300'
                        }`} />
                      </div>
                      {i < tracking.timeline.length - 1 && (
                        <div className={`w-0.5 h-8 ${isCompleted ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                      )}
                    </div>
                    {/* Content */}
                    <div className="pb-6">
                      <p className={`font-medium ${isCompleted || isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {step.title}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Order Details */}
            <div className="p-3 bg-muted/30 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tipo:</span>
                <Badge variant="outline">
                  {tracking.order?.order_type === 'delivery' ? '🚗 Entrega' : 
                   tracking.order?.order_type === 'pickup' ? '🏃 Retirada' : '🍽️ Mesa'}
                </Badge>
              </div>
              {tracking.order?.total && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-bold">{tracking.order.total.toFixed(2)} EUR</span>
                </div>
              )}
              {tracking.order?.items?.length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Itens:</span>
                  <span>{tracking.order.items.reduce((acc, i) => acc + (i.quantity || 1), 0)} item(s)</span>
                </div>
              )}
            </div>

            {/* Restaurant Info */}
            {tracking.restaurant && (
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{tracking.restaurant.name}</p>
                  {tracking.restaurant.phone && (
                    <a 
                      href={`tel:${tracking.restaurant.phone}`}
                      className="text-xs text-muted-foreground flex items-center gap-1 hover:underline"
                    >
                      <Phone className="w-3 h-3" />
                      {tracking.restaurant.phone}
                    </a>
                  )}
                </div>
                {tracking.restaurant.phone && (
                  <a
                    href={`https://wa.me/${tracking.restaurant.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}

            {/* Notifications Toggle */}
            <div className="pt-2">
              {notificationsEnabled ? (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg text-emerald-700">
                  <Bell className="w-5 h-5" />
                  <span className="text-sm">Notificações ativadas para este pedido</span>
                </div>
              ) : (
                <Button
                  onClick={enableNotifications}
                  disabled={subscribing}
                  className="w-full"
                  style={{ backgroundColor: themeColor }}
                  data-testid="enable-notifications-btn"
                >
                  {subscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Bell className="w-4 h-4 mr-2" />
                  )}
                  Ativar Notificações
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Small tracking button for order confirmation
 */
export function TrackOrderButton({ orderId, onClick, themeColor = "#059669" }) {
  return (
    <Button
      onClick={() => onClick?.(orderId)}
      variant="outline"
      className="gap-2"
      data-testid="track-order-btn"
    >
      <Package className="w-4 h-4" />
      Rastrear Pedido
    </Button>
  );
}

/**
 * Hook to subscribe customer for general notifications (promotions)
 */
export function useCustomerNotifications(restaurantId, customerPhone) {
  const [enabled, setEnabled] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    // Check if already subscribed
    const key = `kaiso_push_${restaurantId}_${customerPhone}`;
    setEnabled(localStorage.getItem(key) === 'true');
  }, [restaurantId, customerPhone]);

  const subscribe = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !customerPhone) {
      return false;
    }

    setSubscribing(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setSubscribing(false);
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const vapidRes = await axios.get(`${API_URL}/api/push/vapid-key`);
      const vapidKey = vapidRes.data.public_key;

      if (!vapidKey) {
        setSubscribing(false);
        return false;
      }

      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
      };

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      await axios.post(`${API_URL}/api/push/subscribe-customer-general`, {
        phone: customerPhone,
        restaurant_id: restaurantId,
        subscription: subscription.toJSON()
      });

      const key = `kaiso_push_${restaurantId}_${customerPhone}`;
      localStorage.setItem(key, 'true');
      setEnabled(true);
      setSubscribing(false);
      return true;
    } catch (err) {
      console.error("Push subscription error:", err);
      setSubscribing(false);
      return false;
    }
  };

  return { enabled, subscribing, subscribe };
}

export default OrderTrackingModal;
